import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import FloatingButtons from '../../../components/feature/FloatingButtons';
import Breadcrumb from '../../../components/feature/Breadcrumb';
import InternalLinks from '../../../components/feature/InternalLinks';

export default function MetroEthernetFiberEk() {
  useEffect(() => {
    document.title = 'Metro Ethernet Fiber Ek | Fiber Ek';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Metro Ethernet hatları için profesyonel fiber optik ekleme hizmeti. Fusion splicing, OTDR testi ve 7/24 teknik destek ile kesintisiz bağlantı.');
    }

    // Canonical link
    const canonical = document.createElement('link');
    canonical.rel = 'canonical';
    canonical.href = `${import.meta.env.VITE_SITE_URL}/hizmetlerimiz/metro-ethernet-fiber-ek`;
    document.head.appendChild(canonical);

    // Open Graph tags
    const ogTitle = document.createElement('meta');
    ogTitle.setAttribute('property', 'og:title');
    ogTitle.content = 'Metro Ethernet Fiber Ek | Fiber Ek';
    document.head.appendChild(ogTitle);

    const ogDescription = document.createElement('meta');
    ogDescription.setAttribute('property', 'og:description');
    ogDescription.content = 'Metro Ethernet hatları için profesyonel fiber optik ekleme hizmeti. Fusion splicing, OTDR testi ve 7/24 teknik destek ile kesintisiz bağlantı.';
    document.head.appendChild(ogDescription);

    const ogUrl = document.createElement('meta');
    ogUrl.setAttribute('property', 'og:url');
    ogUrl.content = `${import.meta.env.VITE_SITE_URL}/hizmetlerimiz/metro-ethernet-fiber-ek`;
    document.head.appendChild(ogUrl);

    const ogType = document.createElement('meta');
    ogType.setAttribute('property', 'og:type');
    ogType.content = 'website';
    document.head.appendChild(ogType);

    const ogLocale = document.createElement('meta');
    ogLocale.setAttribute('property', 'og:locale');
    ogLocale.content = 'tr_TR';
    document.head.appendChild(ogLocale);

    // Service JSON-LD
    const schema = document.createElement('script');
    schema.type = 'application/ld+json';
    schema.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Metro Ethernet Fiber Ek",
      "description": "Metro Ethernet hatları için profesyonel fiber optik ekleme hizmeti. Fusion splicing, OTDR testi ve 7/24 teknik destek ile kesintisiz bağlantı.",
      "provider": {
        "@type": "LocalBusiness",
        "name": "Fiber Ek",
        "telephone": "+905537165561",
        "url": import.meta.env.VITE_SITE_URL
      },
      "areaServed": {
        "@type": "Place",
        "name": "İstanbul, Kocaeli, Sakarya, Bursa, Tekirdağ, Yalova"
      },
      "url": `${import.meta.env.VITE_SITE_URL}/hizmetlerimiz/metro-ethernet-fiber-ek`
    });
    document.head.appendChild(schema);

    return () => {
      document.head.removeChild(canonical);
      document.head.removeChild(ogTitle);
      document.head.removeChild(ogDescription);
      document.head.removeChild(ogUrl);
      document.head.removeChild(ogType);
      document.head.removeChild(ogLocale);
      document.head.removeChild(schema);
    };
  }, []);

  const breadcrumbItems = [
    { label: 'Anasayfa', href: '/' },
    { label: 'Hizmetlerimiz', href: '/#hizmetlerimiz' },
    { label: 'Metro Ethernet Fiber Ek' }
  ];

  const faqData = [
    {
      question: "Metro Ethernet nedir ve avantajları nelerdir?",
      answer: "Metro Ethernet, şehir içi yüksek hızlı internet bağlantısı sağlayan teknolojidir. Düşük gecikme, yüksek bant genişliği ve güvenilir bağlantı sunar."
    },
    {
      question: "Fiber ek işlemi ne kadar sürer?",
      answer: "Standart fiber ek işlemi 2-4 saat, karmaşık projelerde 1-2 gün sürebilir. Fusion splicing ile kayıpsız bağlantı sağlarız."
    },
    {
      question: "OTDR test neden yapılır?",
      answer: "Fiber kablonun kalitesini, kayıplarını ve kırılma noktalarını tespit etmek için yapılır. Bağlantı kalitesini garanti eder."
    },
    {
      question: "SC ve LC konnektör farkı nedir?",
      answer: "SC konnektör daha büyük, LC konnektör daha küçük boyuttadır. LC modern ağlarda daha çok tercih edilir."
    },
    {
      question: "OM3 ve OM4 fiber kablo farkları nelerdir?",
      answer: "OM4 daha yüksek bant genişliği ve uzun mesafe desteği sunar. Modern uygulamalarda OM4 tercih edilir."
    }
  ];

  return (
    <>
      <div className="min-h-screen bg-white">
        <Navbar />
        
        {/* Hero Section */}
        <section className="relative pt-24 pb-16 bg-gradient-to-b from-slate-900 to-slate-800">
          <div className="absolute inset-0">
            <img 
              src="https://public.readdy.ai/ai/img_res/c1eb2669b57680b25e6ff6e037405be2.jpg"
              alt="İstanbul Anadolu Yakası"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-slate-900/80 via-slate-800/70 to-slate-900/80"></div>
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <Breadcrumb items={breadcrumbItems} />
            <div className="max-w-4xl mx-auto text-center text-white pt-8">
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                Metro Ethernet Fiber Ek
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 mb-8">
                İstanbul Anadolu Yakası İçin Yüksek Hızlı Metro Ethernet Fiber Bağlantıları
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="tel:+905537165561" 
                  className="px-8 py-4 bg-cyan-500 text-white font-bold rounded-full hover:bg-cyan-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-phone-line mr-2"></i>
                  +90 553 716 55 61
                </a>
                <a 
                  href="https://wa.me/905537165561" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="px-8 py-4 bg-green-500 text-white font-bold rounded-full hover:bg-green-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line mr-2"></i>
                  WhatsApp
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Proje Detayları */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  İstanbul Anadolu Metro Ethernet Projesi
                </h2>
                <p className="text-gray-600 text-lg mb-6">
                  İstanbul Anadolu Yakası'nda bulunan işletmeler için özel olarak tasarlanmış metro ethernet fiber ek projemiz. Yüksek hızlı internet bağlantısı ve güvenilir iletişim altyapısı ihtiyaçlarınızı karşılıyoruz.
                </p>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-center">
                    <i className="ri-check-line text-cyan-500 mr-3 text-xl"></i>
                    <span><strong>Metro ethernet fiber ek</strong> ile yüksek hız</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-cyan-500 mr-3 text-xl"></i>
                    <span><strong>Fiber kablo tamiri</strong> ve bakım hizmeti</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-cyan-500 mr-3 text-xl"></i>
                    <span><strong>Fusion splicing hizmeti</strong> ile kayıpsız ek</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-cyan-500 mr-3 text-xl"></i>
                    <span><strong>OTDR test hizmeti</strong> ile kalite kontrolü</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-cyan-500 mr-3 text-xl"></i>
                    <span><strong>SC LC konnektör sonlandırma</strong> hizmeti</span>
                  </li>
                </ul>
              </div>
              <div>
                <img 
                  src="https://readdy.ai/api/search-image?query=metro%20ethernet%20fiber%20splicing%20work%20in%20Istanbul%20Anadolu%2C%20technician%20using%20fusion%20splicer%20equipment%2C%20professional%20fiber%20optic%20connection%2C%20telecommunications%20room%2C%20organized%20cable%20management&width=800&height=600&seq=metro-ethernet-splice&orientation=landscape"
                  alt="Metro Ethernet Fiber Ek İşlemi"
                  className="w-full h-auto rounded-2xl shadow-xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Metro Ethernet Avantajları */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-16">
              Metro Ethernet Fiber Ek Hizmetlerimiz
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-speed-line text-2xl text-blue-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Yüksek Hız Bağlantı</h3>
                <p className="text-gray-600">
                  10Gbps'e kadar yüksek hızlı metro ethernet bağlantısı. İstanbul Anadolu yakası için özel çözümler.
                </p>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-tools-line text-2xl text-green-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Fiber Kablo Tamiri</h3>
                <p className="text-gray-600">
                  Kesilen ve hasar görmüş fiber kabloların profesyonel tamiri. İstanbul Anadolu bölgesinde hızlı müdahale.
                </p>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-link text-2xl text-purple-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Fusion Splicing</h3>
                <p className="text-gray-600">
                  Kayıpsız fiber ek işlemi ile mükemmel bağlantı kalitesi. OM3 OM4 fiber ek uzman ekip ile yapılır.
                </p>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-pulse-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">OTDR Test Hizmeti</h3>
                <p className="text-gray-600">
                  Fiber kablo kalitesini ölçen OTDR test hizmeti. Bağlantı performansını garanti altına alır.
                </p>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-plug-line text-2xl text-red-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Konnektör Sonlandırma</h3>
                <p className="text-gray-600">
                  SC LC konnektör sonlandırma hizmetleri. Fiber optik sonlandırma işlemleri profesyonel ekip ile.
                </p>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-building-line text-2xl text-cyan-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Bina Fiber Altyapı</h3>
                <p className="text-gray-600">
                  Ofis binaları ve iş merkezleri için bina fiber kablo tamiri ve altyapı kurulum hizmetleri.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* İstanbul Anadolu Hakkında */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <img 
                  src="https://readdy.ai/api/search-image?query=Istanbul%20Anadolu%20side%20business%20district%20with%20modern%20office%20buildings%2C%20Bagdat%20Avenue%2C%20Kadikoy%20commercial%20area%2C%20Asian%20side%20urban%20development%2C%20fiber%20optic%20infrastructure&width=800&height=600&seq=istanbul-anadolu-business&orientation=landscape"
                  alt="İstanbul Anadolu İş Merkezi"
                  className="w-full h-auto rounded-2xl shadow-xl"
                />
              </div>
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  İstanbul Anadolu Fiber Altyapısı
                </h2>
                <p className="text-gray-600 text-lg mb-6">
                  İstanbul Anadolu Yakası, modern iş merkezleri ve teknoloji şirketlerinin yoğun olduğu bir bölgedir. Bu bölgedeki işletmelerin yüksek hızlı internet ihtiyaçlarını metro ethernet teknolojisi ile karşılıyoruz.
                </p>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <i className="ri-building-2-line text-cyan-500 mr-3 text-xl mt-1"></i>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">İş Merkezleri</h4>
                      <p className="text-gray-600">Kadıköy, Ataşehir ve Ümraniye iş bölgeleri</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-global-line text-cyan-500 mr-3 text-xl mt-1"></i>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Yüksek Hız İhtiyacı</h4>
                      <p className="text-gray-600">Teknoloji şirketleri için kritik internet altyapısı</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-timer-line text-cyan-500 mr-3 text-xl mt-1"></i>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Hızlı Müdahale</h4>
                      <p className="text-gray-600">Acil fiber arıza durumlarında anında çözüm</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Teknik Özellikler */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-16">
              Metro Ethernet Teknik Özellikleri
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-dashboard-line text-3xl text-blue-600"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">10Gbps Hız</h3>
                <p className="text-gray-600 text-sm">Ultra yüksek hızlı bağlantı desteği</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-time-line text-3xl text-green-600"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Düşük Gecikme</h3>
                <p className="text-gray-600 text-sm">Minimum latency ile hızlı veri aktarımı</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-shield-check-line text-3xl text-purple-600"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">%99.9 Uptime</h3>
                <p className="text-gray-600 text-sm">Güvenilir ve kesintisiz bağlantı</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-settings-line text-3xl text-orange-600"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Esnek Yapı</h3>
                <p className="text-gray-600 text-sm">İhtiyaçlara göre özelleştirilebilir</p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-16">
              Sıkça Sorulan Sorular
            </h2>
            <div className="max-w-3xl mx-auto space-y-6">
              {faqData.map((faq, index) => (
                <div key={index} className="bg-gray-50 rounded-2xl shadow-lg p-6">
                  <h4 className="text-lg font-bold text-gray-900 mb-3">{faq.question}</h4>
                  <p className="text-gray-600">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-gradient-to-r from-cyan-600 to-blue-600">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Metro Ethernet Fiber Ek İçin İletişime Geçin
            </h2>
            <p className="text-cyan-100 text-xl mb-8 max-w-2xl mx-auto">
              İstanbul Anadolu yakasında metro ethernet fiber bağlantısı için profesyonel hizmet. Hemen teklif alın.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="tel:+905537165561" 
                className="px-8 py-4 bg-white text-cyan-600 font-bold rounded-full hover:bg-gray-100 transition-colors whitespace-nowrap"
              >
                <i className="ri-phone-line mr-2"></i>
                +90 553 716 55 61
              </a>
              <a 
                href="https://wa.me/905537165561" 
                target="_blank" 
                rel="noopener noreferrer"
                className="px-8 py-4 bg-green-500 text-white font-bold rounded-full hover:bg-green-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-whatsapp-line mr-2"></i>
                WhatsApp İletişim
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}